import * as React from "react";
import { LoadingSpinner } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

// ─── Types ────────────────────────────────────────────────────────────────────

interface AgentActivity {
  userId: number;
  displayName: string;
  email: string;
  currentChats: number;
  status: string;
}

interface SiteActivity {
  siteId: number;
  companyName: string;
  activeAgents: AgentActivity[];
  totalAgents: number;
  todayConversations: {
    botOnly: number;
    humanOnly: number;
    botAndHuman: number;
    total: number;
  };
}

interface GlobalStats {
  totalAgentsOnline: number;
  totalSitesActive: number;
  globalConversationsToday: {
    botOnly: number;
    humanOnly: number;
    botAndHuman: number;
    total: number;
  };
}

interface PlatformAlert {
  severity: "error" | "warning" | "info";
  category: string;
  siteId: number;
  title: string;
  detail: string;
  campaignId: number | null;
  ts: string;
}

interface AlertsData {
  generatedAt: string;
  errorCount: number;
  warningCount: number;
  allClear: boolean;
  alerts: PlatformAlert[];
}

// ─── Alerts tab ───────────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  error:   { bar: "bg-red-500",    badge: "bg-red-100 text-red-700 border-red-200",   dot: "bg-red-500",    icon: "🔴" },
  warning: { bar: "bg-amber-400",  badge: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-400", icon: "🟡" },
  info:    { bar: "bg-blue-400",   badge: "bg-blue-100 text-blue-700 border-blue-200",  dot: "bg-blue-400",   icon: "🔵" },
};

function AlertsPanel() {
  const api = useApi();
  const [data, setData] = React.useState<AlertsData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdate, setLastUpdate] = React.useState<Date>(new Date());
  const [filter, setFilter] = React.useState<"all" | "error" | "warning">("all");

  const load = React.useCallback(async () => {
    try {
      const res = await api.admin.get("LiveMonitor/PlatformAlerts");
      const json = await res.json();
      if (json && Array.isArray(json.alerts)) {
        setData(json);
        setLastUpdate(new Date());
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(iv);
  }, [load]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.alerts;
    return data.alerts.filter(a => a.severity === filter);
  }, [data, filter]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="grid grid-cols-3 gap-4">
        <div
          onClick={() => setFilter(filter === "error" ? "all" : "error")}
          className={`rounded-xl p-4 cursor-pointer border-2 transition-all ${filter === "error" ? "border-red-400 bg-red-50" : "border-transparent bg-white shadow-sm hover:shadow"}`}
        >
          <div className="text-3xl font-bold text-red-600">{data?.errorCount ?? 0}</div>
          <div className="text-sm text-slate-600 mt-1">🔴 Errors</div>
        </div>
        <div
          onClick={() => setFilter(filter === "warning" ? "all" : "warning")}
          className={`rounded-xl p-4 cursor-pointer border-2 transition-all ${filter === "warning" ? "border-amber-400 bg-amber-50" : "border-transparent bg-white shadow-sm hover:shadow"}`}
        >
          <div className="text-3xl font-bold text-amber-600">{data?.warningCount ?? 0}</div>
          <div className="text-sm text-slate-600 mt-1">🟡 Warnings</div>
        </div>
        <div className="rounded-xl p-4 bg-white shadow-sm flex flex-col justify-center">
          <div className={`text-sm font-semibold ${data?.allClear ? "text-green-600" : "text-slate-500"}`}>
            {data?.allClear ? "✅ All clear" : "Issues detected"}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Updated {lastUpdate.toLocaleTimeString()}
          </div>
          <button onClick={load} className="text-xs text-blue-600 underline mt-1 text-left">Refresh now</button>
        </div>
      </div>

      {/* Alert list */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-slate-400">
          {data?.allClear
            ? "✅ No issues detected across all sites and services."
            : `No ${filter} alerts.`}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((alert, i) => {
            const s = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.info;
            return (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden flex">
                <div className={`w-1.5 flex-none ${s.bar}`} />
                <div className="flex-1 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${s.badge}`}>
                          {s.icon} {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                          {alert.category}
                        </span>
                        {alert.siteId > 0 && (
                          <span className="text-xs text-slate-400">Site {alert.siteId}</span>
                        )}
                        {alert.campaignId && (
                          <span className="text-xs text-slate-400">Campaign #{alert.campaignId}</span>
                        )}
                      </div>
                      <div className="mt-1.5 font-medium text-slate-800 text-sm">{alert.title}</div>
                      <div className="mt-0.5 text-xs text-slate-500 leading-relaxed">{alert.detail}</div>
                    </div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">
                      {new Date(alert.ts).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        Checks: IVR failure rates · stalled campaigns · high opt-out · email stuck · missing App Settings · auto-refreshes every 60s
      </div>
    </div>
  );
}

// ─── Dashboard tab (existing) ─────────────────────────────────────────────────

function DashboardPanel() {
  const api = useApi();
  const [data, setData] = React.useState<{ sites: SiteActivity[]; global: GlobalStats } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdate, setLastUpdate] = React.useState<Date>(new Date());
  const [refreshInterval, setRefreshInterval] = React.useState(10);

  const fetchData = React.useCallback(async () => {
    try {
      const response = await api.admin.get("LiveMonitor/Dashboard");
      const result = await response.json();
      if (result && result.global && Array.isArray(result.sites)) {
        setData(result);
        setLastUpdate(new Date());
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  React.useEffect(() => { fetchData(); }, [fetchData]);
  React.useEffect(() => {
    const iv = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(iv);
  }, [fetchData, refreshInterval]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  return (
    <>
      <div className="flex justify-end mb-4 items-center gap-2">
        <span className="text-sm text-slate-500">Last updated: {lastUpdate.toLocaleTimeString()}</span>
        <label className="text-sm text-slate-600">Refresh every:</label>
        <select value={refreshInterval} onChange={e => setRefreshInterval(Number(e.target.value))}
          className="px-3 py-1 border border-slate-300 rounded-md text-sm">
          <option value={5}>5s</option>
          <option value={10}>10s</option>
          <option value={30}>30s</option>
          <option value={60}>1m</option>
        </select>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Sites Active",      val: data.global.totalSitesActive,              color: "text-blue-600" },
              { label: "Agents Online",     val: data.global.totalAgentsOnline,              color: "text-green-600" },
              { label: "Total Chats Today", val: data.global.globalConversationsToday.total, color: "text-purple-600" },
              { label: "Avg / Site",        val: data.global.totalSitesActive > 0
                  ? Math.round(data.global.globalConversationsToday.total / data.global.totalSitesActive) : 0, color: "text-orange-600" },
            ].map(({ label, val, color }) => (
              <div key={label} className="bg-white rounded-lg shadow-sm p-5">
                <div className="text-sm text-slate-600 mb-1">{label}</div>
                <div className={`text-3xl font-bold ${color}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            {data.sites.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-500">
                No active sites at the moment
              </div>
            ) : data.sites.map(site => (
              <div key={site.siteId} className="bg-white rounded-lg shadow-sm p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-semibold text-slate-800">{site.companyName}</div>
                    <div className="text-xs text-slate-500">Site {site.siteId}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{site.totalAgents}</div>
                    <div className="text-xs text-slate-500">Agents Online</div>
                  </div>
                </div>
                {site.activeAgents.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {site.activeAgents.map(agent => (
                      <div key={agent.userId} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            agent.status === "Available" ? "bg-green-500" :
                            agent.status === "Busy" ? "bg-red-500" : "bg-yellow-500"
                          }`} />
                          <div>
                            <div className="text-sm font-medium text-slate-800">{agent.displayName}</div>
                            <div className="text-xs text-slate-500">{agent.email}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">{agent.currentChats}</div>
                          <div className="text-xs text-slate-500">chats</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ─── Synthetic Monitoring tab ─────────────────────────────────────────────────

interface SyntheticProbeResult {
  testName: string;
  status: string;
  durationMs: number;
  errorMessage?: string;
  timestamp: string;
  passRate24h?: number;
  totalRuns24h?: number;
}

interface SyntheticData {
  enabled: boolean;
  circuitBreaker: { isOpen: boolean; consecutiveFailures: number };
  testResults: SyntheticProbeResult[];
  totalResultsStored?: number;
  historyDays?: number;
}

function SyntheticPanel() {
  const api = useApi();
  const [data, setData] = React.useState<SyntheticData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState<string | null>(null);
  const [detail, setDetail] = React.useState<any>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [lastUpdate, setLastUpdate] = React.useState(new Date());

  const load = React.useCallback(async () => {
    try {
      const res = await api.messaging.get("Diagnostics/synthetic");
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date());
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api]);

  const loadDetail = React.useCallback(async (testName: string) => {
    setSelected(testName);
    setDetailLoading(true);
    try {
      const res = await api.messaging.get(`Diagnostics/synthetic/${testName}`);
      if (res.ok) setDetail(await res.json());
    } catch { /* non-fatal */ }
    finally { setDetailLoading(false); }
  }, [api]);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  const probes = data?.testResults ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {data?.circuitBreaker?.isOpen && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
              ⚡ Circuit breaker OPEN — {data.circuitBreaker.consecutiveFailures} consecutive failures
            </span>
          )}
          {!data?.circuitBreaker?.isOpen && probes.length > 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
              ✅ Circuit breaker closed
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">Updated {lastUpdate.toLocaleTimeString()}</span>
          <button onClick={load} className="text-xs text-blue-600 underline">Refresh</button>
        </div>
      </div>

      {probes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-slate-400 shadow-sm">
          No synthetic test results. Probes run on a cron schedule.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {probes.map(probe => {
            const pass = probe.status === "Pass";
            return (
              <button
                key={probe.testName}
                type="button"
                onClick={() => loadDetail(probe.testName)}
                className={`text-left rounded-xl border-2 p-4 shadow-sm hover:shadow transition-all ${
                  selected === probe.testName ? "ring-2 ring-blue-400" : ""
                } ${pass ? "bg-white border-green-200" : "bg-red-50 border-red-300"}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-semibold text-slate-800 text-sm">{probe.testName}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    pass ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>{pass ? "✓ Pass" : "✗ Fail"}</span>
                </div>
                <div className="text-xs text-slate-500 space-y-1">
                  <div>{probe.durationMs}ms</div>
                  {probe.passRate24h !== undefined && (
                    <div className={probe.passRate24h < 90 ? "text-red-600 font-medium" : ""}>
                      24h: {(probe.passRate24h * 100).toFixed(1)}% ({probe.totalRuns24h} runs)
                    </div>
                  )}
                  {!pass && probe.errorMessage && (
                    <div className="text-red-600 text-xs truncate" title={probe.errorMessage}>
                      {probe.errorMessage}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
            <span className="font-semibold text-slate-800">{selected}</span>
            <button onClick={() => { setSelected(null); setDetail(null); }} className="text-slate-400 hover:text-slate-700 text-lg leading-none">✕</button>
          </div>
          {detailLoading ? (
            <div className="flex justify-center py-8"><LoadingSpinner /></div>
          ) : detail ? (
            <div className="p-4 space-y-4">
              {/* Runbook — what broke + how to fix */}
              {detail.runbook && (
                <div className={`rounded-lg border p-4 ${(detail.recentFailures ?? []).length > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-sm font-semibold ${(detail.recentFailures ?? []).length > 0 ? "text-red-700" : "text-slate-700"}`}>
                      {(detail.recentFailures ?? []).length > 0 ? "⚠ What broke & how to fix it" : "Runbook"}
                    </h4>
                    <span className="text-xs text-slate-400">Owner: {detail.runbook.owner}</span>
                  </div>
                  <p className={`text-xs mb-3 ${(detail.recentFailures ?? []).length > 0 ? "text-red-800 font-medium" : "text-slate-600"}`}>
                    <span className="font-semibold">Impact: </span>{detail.runbook.impact}
                  </p>
                  <ol className="space-y-1.5">
                    {(detail.runbook.steps ?? []).map((step: string, i: number) => (
                      <li key={i} className="flex gap-2 text-xs text-slate-700">
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${(detail.recentFailures ?? []).length > 0 ? "bg-red-200 text-red-700" : "bg-slate-200 text-slate-600"}`}>
                          {i + 1}
                        </span>
                        <span>{step.replace(/^\d+\.\s*/, "")}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Recent failures */}
              {(detail.recentFailures ?? []).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-600 mb-2">Recent Failures</h4>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {(detail.recentFailures ?? []).map((f: any, i: number) => (
                      <div key={i} className="bg-red-50 border border-red-100 rounded px-3 py-2 text-xs">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-red-700 font-medium">{new Date(f.timestamp).toLocaleString()}</span>
                          <span className="text-slate-400">{f.durationMs}ms</span>
                        </div>
                        <div className="text-red-800 break-all">{f.errorMessage ?? "No error message"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Run history table */}
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Last {(detail.recentResults ?? []).length} runs</h4>
                <div className="overflow-y-auto max-h-48 rounded border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="border-b border-slate-200">
                        <th className="px-3 py-2 text-left text-slate-500 font-medium">Status</th>
                        <th className="px-3 py-2 text-left text-slate-500 font-medium">Duration</th>
                        <th className="px-3 py-2 text-left text-slate-500 font-medium">Time</th>
                        <th className="px-3 py-2 text-left text-slate-500 font-medium">Message</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {(detail.recentResults ?? []).map((r: any, i: number) => (
                        <tr key={i} className={r.status === "Pass" ? "" : "bg-red-50"}>
                          <td className={`px-3 py-2 font-semibold ${r.status === "Pass" ? "text-green-600" : "text-red-600"}`}>
                            {r.status === "Pass" ? "✓" : "✗"} {r.status}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{r.durationMs}ms</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                            {new Date(r.timestamp).toLocaleTimeString()}
                          </td>
                          <td className={`px-3 py-2 max-w-xs truncate ${r.status === "Pass" ? "text-slate-400" : "text-red-600"}`} title={r.errorMessage ?? ""}>
                            {r.errorMessage ?? ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        Probes: ApiHealth · ContactsLoad · InboxLoad · ConversationE2E · DatabaseWriteRead · RedisConnectivity · SignalRConnectivity · MigrationHealth · SchemaValidation · CrmHealth · IvrReadiness · IvrAlertMonitor · SmsReadiness · SmsDelivery — auto-refreshes every 60s
      </div>
    </div>
  );
}

// ─── Keys Status tab ─────────────────────────────────────────────────────────
// Fetches the messaging service's platform-keys/status endpoint.
// Shows green/red for every API key. Missing keys are highlighted prominently.
// Use this before every production deploy to catch missing secrets early.

interface KeyEntry {
  name: string;
  category: string;
  isConfigured: boolean;
  maskedValue: string | null;
  required: boolean;
  description: string;
  providerUrl: string;
  rotationInstructions: string;
  appSettingName: string;
}

function KeysPanel() {
  const api = useApi();
  const [keys, setKeys] = React.useState<KeyEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = React.useState<Date | null>(null);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setError(null);
      const res = await api.messaging.get("SuperAdmin/platform-keys/status");
      if (!res.ok) { setError(`Failed to load key status (${res.status})`); return; }
      const json = await res.json();
      if (Array.isArray(json)) {
        setKeys(json);
        setLastUpdate(new Date());
      }
    } catch (e) {
      setError("Could not reach messaging service key status endpoint.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>;

  const missing  = keys.filter(k => !k.isConfigured);
  const missingRequired = missing.filter(k => k.required);
  const missingOptional = missing.filter(k => !k.required);
  const configured = keys.filter(k => k.isConfigured);

  const categories = Array.from(new Set(keys.map(k => k.category)));

  return (
    <div className="space-y-6">
      {/* Summary banner */}
      <div className={`rounded-2xl p-5 border-2 flex items-start gap-4 ${
        missingRequired.length > 0
          ? "border-red-300 bg-red-50"
          : missingOptional.length > 0
          ? "border-slate-200 bg-slate-50"
          : "border-green-300 bg-green-50"
      }`}>
        <span className="text-3xl">
          {missingRequired.length > 0 ? "🔴" : missingOptional.length > 0 ? "ℹ️" : "✅"}
        </span>
        <div>
          <p className="font-bold text-slate-800 text-lg">
            {missingRequired.length > 0
              ? `${missingRequired.length} required key${missingRequired.length > 1 ? "s" : ""} missing — DO NOT deploy to production`
              : missingOptional.length > 0
              ? `All required keys configured — ${missingOptional.length} optional feature${missingOptional.length > 1 ? "s" : ""} not set up`
              : `All ${configured.length} keys configured ✓`}
          </p>
          <p className="text-sm text-slate-600 mt-0.5">
            {configured.length} of {keys.length} keys set.
            {lastUpdate && ` Last checked ${lastUpdate.toLocaleTimeString()}.`}
          </p>
          {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        </div>
        <button onClick={load} className="ml-auto text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded px-2 py-1" aria-label="Refresh">
          ↻ Refresh
        </button>
      </div>

      {/* Missing required — red block */}
      {missingRequired.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold text-red-700 uppercase tracking-wide flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full" /> Required — Missing
          </h2>
          {missingRequired.map(k => (
            <KeyCard key={k.appSettingName} entry={k} expanded={expanded} setExpanded={setExpanded} />
          ))}
        </div>
      )}

      {/* Missing optional — amber block */}
      {missingOptional.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold text-amber-700 uppercase tracking-wide flex items-center gap-2">
            <span className="w-2 h-2 bg-amber-400 rounded-full" /> Optional — Not Configured
          </h2>
          {missingOptional.map(k => (
            <KeyCard key={k.appSettingName} entry={k} expanded={expanded} setExpanded={setExpanded} />
          ))}
        </div>
      )}

      {/* Configured — grouped by category */}
      {categories.map(cat => {
        const catKeys = configured.filter(k => k.category === cat);
        if (catKeys.length === 0) return null;
        return (
          <div key={cat} className="space-y-1">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{cat}</h2>
            {catKeys.map(k => (
              <KeyCard key={k.appSettingName} entry={k} expanded={expanded} setExpanded={setExpanded} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function KeyCard({ entry, expanded, setExpanded }: { entry: KeyEntry; expanded: string | null; setExpanded: (v: string | null) => void }) {
  const isOpen = expanded === entry.appSettingName;
  return (
    <div className={`rounded-xl border p-3 cursor-pointer transition-all ${
      !entry.isConfigured && entry.required
        ? "border-red-300 bg-red-50"
        : !entry.isConfigured
        ? "border-slate-200 bg-slate-50 hover:border-slate-300"
        : "border-slate-200 bg-white hover:border-slate-300"
    }`} onClick={() => setExpanded(isOpen ? null : entry.appSettingName)}>
      <div className="flex items-center gap-3">
        <span className="text-base">{entry.isConfigured ? "✅" : entry.required ? "🔴" : "⬜"}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{entry.name}</span>
            <span className="text-xs text-slate-400 font-mono">{entry.appSettingName}</span>
            {entry.required && <span className="text-xs bg-red-100 text-red-700 px-1.5 rounded font-medium">required</span>}
          </div>
          <p className="text-xs text-slate-500 truncate">{entry.description}</p>
        </div>
        {entry.isConfigured && entry.maskedValue && (
          <span className="text-xs font-mono text-slate-400 flex-shrink-0">{entry.maskedValue}</span>
        )}
        <span className="text-slate-400 text-xs">{isOpen ? "▲" : "▼"}</span>
      </div>
      {isOpen && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-700">
          <p><span className="font-semibold">How to fix:</span> {entry.rotationInstructions}</p>
          <p>
            <span className="font-semibold">Provider: </span>
            <a href={entry.providerUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline" onClick={e => e.stopPropagation()}>
              {entry.providerUrl}
            </a>
          </p>
        </div>
      )}
    </div>
  );
}

// ─── API Notices tab ─────────────────────────────────────────────────────────
// Dismissible notices about API deprecations/changes detected automatically
// from integration response headers (Shopify, BigCommerce, HubSpot, etc.).
// Each notice is shown once — dismiss with ✕ to acknowledge.

interface ApiNotice {
  id: number;
  integration: string;
  noticeType: string;
  details: string;
  apiVersion: string | null;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  emailSentAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  isNew: boolean;
}

const NOTICE_TYPE_STYLES: Record<string, { badge: string; icon: string }> = {
  DeprecatedField:    { badge: "bg-amber-100 text-amber-700 border-amber-200",  icon: "⚠️" },
  UnsupportedVersion: { badge: "bg-red-100 text-red-700 border-red-200",        icon: "🔴" },
  ApiChange:          { badge: "bg-blue-100 text-blue-700 border-blue-200",     icon: "🔵" },
  VersionSunset:      { badge: "bg-orange-100 text-orange-700 border-orange-200", icon: "🌅" },
};

function ApiNoticesPanel() {
  const api = useApi();
  const [notices, setNotices] = React.useState<ApiNotice[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showAcknowledged, setShowAcknowledged] = React.useState(false);
  const [dismissing, setDismissing] = React.useState<Set<number>>(new Set());

  const load = React.useCallback(async () => {
    try {
      const res = await api.messaging.get(`SuperAdmin/ApiDeprecationNotices?includeAcknowledged=${showAcknowledged}`);
      if (res.ok) {
        const json = await res.json();
        setNotices(Array.isArray(json?.notices) ? json.notices : []);
      }
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api, showAcknowledged]);

  React.useEffect(() => { load(); }, [load]);

  const dismiss = React.useCallback(async (id: number) => {
    setDismissing(prev => new Set(prev).add(id));
    try {
      const res = await api.messaging.post(`SuperAdmin/ApiDeprecationNotices/${id}/acknowledge`, {});
      if (res.ok) {
        setNotices(prev => showAcknowledged
          ? prev.map(n => n.id === id ? { ...n, acknowledgedAt: new Date().toISOString() } : n)
          : prev.filter(n => n.id !== id));
      }
    } catch { /* non-fatal */ }
    finally { setDismissing(prev => { const s = new Set(prev); s.delete(id); return s; }); }
  }, [api, showAcknowledged]);

  const unacked = notices.filter(n => !n.acknowledgedAt);

  if (loading) return <div className="flex justify-center py-16"><LoadingSpinner /></div>;

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">
            API Change Notices
            {unacked.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold border border-amber-200">
                {unacked.length} pending
              </span>
            )}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Detected from integration API response headers. Email sent to management@velaro.com on first detection.
            Dismiss with ✕ once reviewed — will not reappear for the same notice.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showAcknowledged}
              onChange={e => setShowAcknowledged(e.target.checked)}
              className="rounded border-slate-300" />
            Show acknowledged
          </label>
          <button onClick={load}
            className="text-xs text-blue-600 border border-blue-200 rounded px-3 py-1 hover:bg-blue-50">
            ↻ Refresh
          </button>
        </div>
      </div>

      {notices.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="text-3xl mb-3">✅</div>
          <div className="font-medium text-slate-700">No API change notices</div>
          <div className="text-sm text-slate-400 mt-1">
            Velaro automatically detects API deprecation signals from Shopify, BigCommerce, HubSpot, and other integrations.
            Any notices will appear here and be emailed to management@velaro.com.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {notices.map(notice => {
            const style = NOTICE_TYPE_STYLES[notice.noticeType] ?? { badge: "bg-slate-100 text-slate-600 border-slate-200", icon: "ℹ️" };
            const isAcked = !!notice.acknowledgedAt;
            return (
              <div key={notice.id}
                className={`bg-white rounded-xl shadow-sm overflow-hidden border ${isAcked ? "opacity-60 border-slate-100" : "border-transparent"}`}>
                <div className="flex">
                  <div className={`w-1.5 flex-none ${isAcked ? "bg-slate-200" : notice.noticeType === "UnsupportedVersion" ? "bg-red-500" : notice.noticeType === "VersionSunset" ? "bg-orange-400" : "bg-amber-400"}`} />
                  <div className="flex-1 px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        {/* Badges row */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                            {notice.integration}
                          </span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${style.badge}`}>
                            {style.icon} {notice.noticeType}
                          </span>
                          {notice.apiVersion && (
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200 font-mono">
                              v{notice.apiVersion}
                            </span>
                          )}
                          {notice.isNew && !isAcked && (
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200 font-bold">
                              NEW
                            </span>
                          )}
                          {notice.occurrenceCount > 1 && (
                            <span className="text-xs text-slate-400">{notice.occurrenceCount.toLocaleString()} occurrences</span>
                          )}
                        </div>

                        {/* Details */}
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed bg-slate-50 rounded px-3 py-2 border border-slate-100">
                          {notice.details}
                        </pre>

                        {/* Metadata row */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>First seen: {new Date(notice.firstSeenAt).toLocaleString()}</span>
                          <span>Last: {new Date(notice.lastSeenAt).toLocaleString()}</span>
                          {notice.emailSentAt && <span>✉️ Email sent {new Date(notice.emailSentAt).toLocaleString()}</span>}
                          {isAcked && <span className="text-green-600 font-medium">✓ Acknowledged by {notice.acknowledgedBy}</span>}
                        </div>
                      </div>

                      {/* Dismiss button */}
                      {!isAcked && (
                        <button
                          type="button"
                          aria-label="Dismiss this API notice"
                          onClick={() => dismiss(notice.id)}
                          disabled={dismissing.has(notice.id)}
                          className="flex-none text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded-full w-7 h-7 flex items-center justify-center transition-colors disabled:opacity-40"
                        >
                          {dismissing.has(notice.id) ? "…" : "✕"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        Monitors: Shopify · BigCommerce · HubSpot · NetSuite · and all other integrations —
        headers checked: X-Shopify-API-Deprecated-Reason, Deprecation (RFC-8594), Sunset
      </div>
    </div>
  );
}

// ─── Routing Diagnostics tab ──────────────────────────────────────────────────

interface RoutingSite {
  siteId: number;
  companyName: string;
  extraLoggingEnabled: boolean;
  eventCount: number;
  failureCount: number;
}

interface RoutingAgent {
  userId: number;
  velaroUserId: number;
  displayName: string;
  email: string;
  status: string;
  maxChats: number;
  licenseStatus: string;
}

interface RoutingTeam {
  teamId: number;
  name: string;
  velaroGroupId: number;
  isDefault: boolean;
}

interface RoutingFailure {
  id: number;
  conversationId: number | null;
  occurredAt: string;
  trigger: string;
  missDetail: string | null;
  agentsOnline: number | null;
  agentsAvailable: number | null;
  agentsBusy: number | null;
  conditionsEvaluated: number;
}

interface OutcomeGroup { outcome: string; count: number }
interface TriggerGroup { trigger: string; count: number }
interface RuleGroup { ruleId: number; ruleName: string; count: number }
interface FailureSnapshot { pattern: string; count: number; example: { agentsOnline: number; agentsAvailable: number; agentsBusy: number } }
interface HourlyBucket { hour: number; total: number; failures: number }

interface RoutingDiagData {
  siteId: number;
  companyName: string;
  extraLoggingEnabled: boolean;
  extraLoggingExpiresAt: string | null;
  period: { days: number; since: string; until: string };
  summary: {
    totalEvents: number;
    failures: number;
    failureRate: number;
    byOutcome: OutcomeGroup[];
    byTrigger: TriggerGroup[];
    topRules: RuleGroup[];
    failureAgentSnapshots: FailureSnapshot[];
  };
  recentFailures: RoutingFailure[];
  hourlyPattern: HourlyBucket[];
  agents: RoutingAgent[];
  teams: RoutingTeam[];
  recommendations: string[];
}

const OUTCOME_COLORS: Record<string, string> = {
  routed_to_team: "bg-green-100 text-green-700 border-green-200",
  workflow_started: "bg-blue-100 text-blue-700 border-blue-200",
  no_match: "bg-red-100 text-red-700 border-red-200",
  unknown: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_DOT: Record<string, string> = {
  Available: "bg-green-500",
  Busy: "bg-red-500",
  Away: "bg-yellow-500",
  Unknown: "bg-slate-400",
};

function RoutingDiagnosticsPanel() {
  const api = useApi();
  const [sites, setSites] = React.useState<RoutingSite[]>([]);
  const [allSites, setAllSites] = React.useState<RoutingSite[]>([]);
  const [selectedSite, setSelectedSite] = React.useState<number>(0);
  const [days, setDays] = React.useState(7);
  const [data, setData] = React.useState<RoutingDiagData | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [sitesLoading, setSitesLoading] = React.useState(true);
  const [toggling, setToggling] = React.useState(false);
  const [loggingDuration, setLoggingDuration] = React.useState(7);
  const [simulating, setSimulating] = React.useState(false);
  const [simResult, setSimResult] = React.useState<any>(null);
  const [simGroupId, setSimGroupId] = React.useState(0);
  const [siteSearch, setSiteSearch] = React.useState("");

  // Load sites list
  const loadSites = React.useCallback(async () => {
    try {
      const res = await api.admin.get("LiveMonitor/RoutingSites?days=30");
      if (res.ok) {
        const json = await res.json();
        setSites(Array.isArray(json?.activeSites) ? json.activeSites : []);
        setAllSites(Array.isArray(json?.allSites) ? json.allSites : []);
      }
    } catch { /* non-fatal */ }
    finally { setSitesLoading(false); }
  }, [api]);

  React.useEffect(() => { loadSites(); }, [loadSites]);

  // Load diagnostics for selected site
  const loadDiag = React.useCallback(async () => {
    if (selectedSite <= 0) return;
    setLoading(true);
    setData(null);
    try {
      const res = await api.admin.get(`LiveMonitor/RoutingDiagnostics?siteId=${selectedSite}&days=${days}`);
      if (res.ok) setData(await res.json());
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [api, selectedSite, days]);

  React.useEffect(() => { loadDiag(); }, [loadDiag]);

  // Toggle extra logging
  const toggleLogging = async () => {
    if (!data) return;
    const enabling = !data.extraLoggingEnabled;
    setToggling(true);
    try {
      const res = await api.admin.post("LiveMonitor/ToggleExtraLogging", {
        siteId: selectedSite,
        enable: enabling,
        durationDays: enabling ? loggingDuration : 0,
      });
      if (res.ok) {
        const json = await res.json();
        const newEnabled = json?.enableExtraRoutingLog ?? enabling;
        const newExpiry = json?.expiresAt ?? null;
        setData(prev => prev ? { ...prev, extraLoggingEnabled: newEnabled, extraLoggingExpiresAt: newExpiry } : prev);
        setSites(prev => prev.map(s => s.siteId === selectedSite ? { ...s, extraLoggingEnabled: newEnabled } : s));
        setAllSites(prev => prev.map(s => s.siteId === selectedSite ? { ...s, extraLoggingEnabled: newEnabled } : s));
      }
    } catch { /* non-fatal */ }
    finally { setToggling(false); }
  };

  // Run simulation
  const runSimulation = async () => {
    setSimulating(true);
    setSimResult(null);
    try {
      const res = await api.admin.post("LiveMonitor/RoutingSimulation", {
        siteId: selectedSite,
        groupId: simGroupId,
      });
      if (res.ok) setSimResult(await res.json());
    } catch { /* non-fatal */ }
    finally { setSimulating(false); }
  };

  const filteredSites = siteSearch
    ? allSites.filter(s =>
        s.companyName.toLowerCase().includes(siteSearch.toLowerCase()) ||
        s.siteId.toString().includes(siteSearch))
    : allSites;

  if (sitesLoading) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      {/* ── Site Picker ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Select Site</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name or site ID..."
                value={siteSearch}
                onChange={e => setSiteSearch(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              />
              {siteSearch && filteredSites.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                  {filteredSites.slice(0, 20).map(s => (
                    <button
                      key={s.siteId}
                      type="button"
                      onClick={() => { setSelectedSite(s.siteId); setSiteSearch(""); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 flex items-center justify-between text-sm border-b border-slate-50 last:border-0"
                    >
                      <span>
                        <span className="font-medium text-slate-800">{s.companyName}</span>
                        <span className="text-slate-400 ml-2">#{s.siteId}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        {s.extraLoggingEnabled && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Extra Log</span>
                        )}
                        {s.failureCount > 0 && (
                          <span className="text-xs text-red-600">{s.failureCount} failures</span>
                        )}
                        <span className="text-xs text-slate-400">{s.eventCount} events</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick-pick: sites with most failures */}
          {sites.filter(s => s.failureCount > 0).length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Sites with Failures</label>
              <div className="flex gap-1 flex-wrap">
                {sites.filter(s => s.failureCount > 0).slice(0, 5).map(s => (
                  <button
                    key={s.siteId}
                    type="button"
                    onClick={() => setSelectedSite(s.siteId)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      selectedSite === s.siteId
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                    }`}
                  >
                    {s.companyName} ({s.failureCount})
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Period</label>
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
              <option value={1}>Last 24h</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
        </div>

        {selectedSite > 0 && data && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-lg font-bold text-slate-800">{data.companyName}</span>
                <span className="text-slate-400 ml-2">Site #{data.siteId}</span>
              </div>
              <div className="flex items-center gap-3">
                {/* Duration picker — only show when logging is OFF (about to enable) */}
                {!data.extraLoggingEnabled && (
                  <select value={loggingDuration} onChange={e => setLoggingDuration(Number(e.target.value))}
                    className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>1 week</option>
                    <option value={14}>2 weeks</option>
                    <option value={30}>30 days</option>
                  </select>
                )}
                <button
                  type="button"
                  onClick={toggleLogging}
                  disabled={toggling}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                    data.extraLoggingEnabled
                      ? "bg-amber-50 border-amber-400 text-amber-700 hover:bg-amber-100"
                      : "bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100"
                  } disabled:opacity-50`}
                  aria-label={data.extraLoggingEnabled ? "Disable extra routing logging" : "Enable extra routing logging"}
                >
                  <div className={`w-10 h-5 rounded-full relative transition-colors ${data.extraLoggingEnabled ? "bg-amber-400" : "bg-slate-300"}`}>
                    <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${data.extraLoggingEnabled ? "left-5" : "left-0.5"}`} />
                  </div>
                  <span>{data.extraLoggingEnabled ? "Extra Logging ON" : "Extra Logging OFF"}</span>
                </button>
                <button onClick={loadDiag} className="text-xs text-blue-600 border border-blue-200 rounded px-3 py-2 hover:bg-blue-50" aria-label="Refresh diagnostics">
                  Refresh
                </button>
              </div>
            </div>
            {data.extraLoggingEnabled && data.extraLoggingExpiresAt && (
              <div className="mt-2 text-xs text-amber-600">
                Extra logging expires {new Date(data.extraLoggingExpiresAt).toLocaleDateString()} at {new Date(data.extraLoggingExpiresAt).toLocaleTimeString()} (
                {(() => {
                  const diff = new Date(data.extraLoggingExpiresAt!).getTime() - Date.now();
                  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
                  return days > 0 ? `${days} day${days !== 1 ? "s" : ""} remaining` : "expired";
                })()})
              </div>
            )}
          </div>
        )}
      </div>

      {selectedSite <= 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-slate-400">
          Select a site above to view routing diagnostics. Search by company name or site ID.
        </div>
      )}

      {loading && <div className="flex justify-center py-12"><LoadingSpinner /></div>}

      {data && !loading && (
        <>
          {/* ── Summary Cards ─────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="text-sm text-slate-600 mb-1">Total Events</div>
              <div className="text-3xl font-bold text-blue-600">{data.summary.totalEvents}</div>
              <div className="text-xs text-slate-400 mt-1">{data.period.days} day period</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="text-sm text-slate-600 mb-1">Failures</div>
              <div className={`text-3xl font-bold ${data.summary.failures > 0 ? "text-red-600" : "text-green-600"}`}>
                {data.summary.failures}
              </div>
              <div className="text-xs text-slate-400 mt-1">{data.summary.failureRate}% failure rate</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="text-sm text-slate-600 mb-1">Agents</div>
              <div className="text-3xl font-bold text-purple-600">{data.agents.length}</div>
              <div className="text-xs text-slate-400 mt-1">
                {data.agents.filter(a => a.status === "Available").length} available
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5">
              <div className="text-sm text-slate-600 mb-1">Teams</div>
              <div className="text-3xl font-bold text-orange-600">{data.teams.length}</div>
              <div className="text-xs text-slate-400 mt-1">
                {data.teams.filter(t => t.isDefault).length} default
              </div>
            </div>
          </div>

          {/* ── Recommendations ───────────────────────────────────── */}
          {data.recommendations.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 space-y-2">
              <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Recommendations</h3>
              {data.recommendations.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-amber-900">
                  <span className="text-amber-500 mt-0.5 flex-none">-</span>
                  <span>{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Outcome & Trigger Breakdown ───────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* By Outcome */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-3">By Outcome</h3>
              {data.summary.byOutcome.length === 0 ? (
                <div className="text-sm text-slate-400">No events</div>
              ) : (
                <div className="space-y-2">
                  {data.summary.byOutcome.map(o => {
                    const pct = data.summary.totalEvents > 0 ? Math.round(o.count / data.summary.totalEvents * 100) : 0;
                    const style = OUTCOME_COLORS[o.outcome] ?? OUTCOME_COLORS.unknown;
                    return (
                      <div key={o.outcome} className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded border min-w-[120px] text-center ${style}`}>
                          {o.outcome}
                        </span>
                        <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${o.outcome === "no_match" ? "bg-red-400" : o.outcome === "routed_to_team" ? "bg-green-400" : "bg-blue-400"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 min-w-[50px] text-right">{o.count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* By Trigger */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-3">By Trigger</h3>
              {data.summary.byTrigger.length === 0 ? (
                <div className="text-sm text-slate-400">No events</div>
              ) : (
                <div className="space-y-2">
                  {data.summary.byTrigger.map(t => (
                    <div key={t.trigger} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">{t.trigger}</span>
                      <span className="text-slate-500">{t.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Top Rules ──────────────────────────────────────────── */}
          {data.summary.topRules.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Top Routing Rules</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.summary.topRules.map(r => (
                  <div key={r.ruleId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-slate-800">{r.ruleName ?? `Rule #${r.ruleId}`}</div>
                      <div className="text-xs text-slate-400">ID: {r.ruleId}</div>
                    </div>
                    <div className="text-lg font-bold text-blue-600">{r.count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Failure Agent Snapshots ─────────────────────────────── */}
          {data.summary.failureAgentSnapshots.length > 0 && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
              <h3 className="text-sm font-bold text-red-800 mb-3">Failure Patterns (Agent State at Routing Time)</h3>
              <div className="space-y-3">
                {data.summary.failureAgentSnapshots.map((s, i) => (
                  <div key={i} className="flex items-center gap-4 bg-white rounded-lg p-3 border border-red-100">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                      s.pattern === "all_offline" ? "bg-slate-200 text-slate-700" :
                      s.pattern === "online_but_unavailable" ? "bg-amber-100 text-amber-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>
                      {s.pattern.replace(/_/g, " ")}
                    </span>
                    <span className="text-sm text-slate-600">{s.count}x</span>
                    <span className="text-xs text-slate-400">
                      (example: {s.example.agentsOnline} online, {s.example.agentsAvailable} available, {s.example.agentsBusy} busy)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Agent Grid ──────────────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">
              Agents ({data.agents.length})
              <span className="font-normal text-slate-400 ml-2">DB status — for real-time, check messaging API</span>
            </h3>
            {data.agents.length === 0 ? (
              <div className="text-sm text-slate-400">No agents provisioned for this site</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="px-3 py-2 text-xs font-medium text-slate-500">Agent</th>
                      <th className="px-3 py-2 text-xs font-medium text-slate-500">Email</th>
                      <th className="px-3 py-2 text-xs font-medium text-slate-500">Status</th>
                      <th className="px-3 py-2 text-xs font-medium text-slate-500">Max Chats</th>
                      <th className="px-3 py-2 text-xs font-medium text-slate-500">License</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.agents.map(a => (
                      <tr key={a.userId} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-800">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full flex-none ${STATUS_DOT[a.status] ?? STATUS_DOT.Unknown}`} />
                            {a.displayName}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{a.email}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                            a.status === "Available" ? "bg-green-100 text-green-700" :
                            a.status === "Busy" ? "bg-red-100 text-red-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {a.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{a.maxChats || "Unlimited"}</td>
                        <td className="px-3 py-2 text-slate-500">{a.licenseStatus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Teams ───────────────────────────────────────────────── */}
          {data.teams.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Teams / Groups</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {data.teams.map(t => (
                  <div key={t.teamId} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-800">{t.name}</span>
                      {t.isDefault && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Default</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      V10 GroupId: {t.velaroGroupId} | Team ID: {t.teamId}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Recent Failures ─────────────────────────────────────── */}
          {data.recentFailures.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-bold text-red-700 mb-3">Recent Failures (last 20)</h3>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="border-b border-slate-200 text-left">
                      <th className="px-3 py-2 font-medium text-slate-500">Time</th>
                      <th className="px-3 py-2 font-medium text-slate-500">Conv</th>
                      <th className="px-3 py-2 font-medium text-slate-500">Trigger</th>
                      <th className="px-3 py-2 font-medium text-slate-500">Online</th>
                      <th className="px-3 py-2 font-medium text-slate-500">Avail</th>
                      <th className="px-3 py-2 font-medium text-slate-500">Busy</th>
                      <th className="px-3 py-2 font-medium text-slate-500">Miss Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.recentFailures.map(f => (
                      <tr key={f.id} className="hover:bg-red-50">
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                          {new Date(f.occurredAt).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-600">{f.conversationId ?? "-"}</td>
                        <td className="px-3 py-2">
                          <span className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">{f.trigger}</span>
                        </td>
                        <td className={`px-3 py-2 font-bold ${f.agentsOnline === 0 ? "text-red-600" : "text-green-600"}`}>
                          {f.agentsOnline ?? "-"}
                        </td>
                        <td className={`px-3 py-2 font-bold ${f.agentsAvailable === 0 ? "text-red-600" : "text-green-600"}`}>
                          {f.agentsAvailable ?? "-"}
                        </td>
                        <td className="px-3 py-2 text-slate-500">{f.agentsBusy ?? "-"}</td>
                        <td className="px-3 py-2 text-red-600 max-w-xs truncate" title={f.missDetail ?? ""}>
                          {f.missDetail ?? "No detail recorded"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Routing Simulation ──────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-bold text-slate-700 mb-3">Routing Simulation (Dry Run)</h3>
            <p className="text-xs text-slate-400 mb-3">
              Simulate what would happen if a chat came in right now for this site. GroupId=0 means "any group" (default widget behavior).
            </p>
            <div className="flex items-end gap-3">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Group ID</label>
                <select value={simGroupId} onChange={e => setSimGroupId(Number(e.target.value))}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value={0}>0 — All Groups (Default)</option>
                  {data.teams.map(t => (
                    <option key={t.teamId} value={t.velaroGroupId}>{t.velaroGroupId} — {t.name}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={runSimulation}
                disabled={simulating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                aria-label="Run routing simulation"
              >
                {simulating ? "Simulating..." : "Run Simulation"}
              </button>
            </div>

            {simResult && (
              <div className="mt-4 bg-slate-50 rounded-lg p-4 border border-slate-200">
                <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono overflow-x-auto max-h-80 overflow-y-auto">
                  {JSON.stringify(simResult, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* ── Hourly Pattern ──────────────────────────────────────── */}
          {data.hourlyPattern.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-3">24h Hourly Pattern (UTC)</h3>
              <div className="flex items-end gap-1 h-32">
                {Array.from({ length: 24 }, (_, h) => {
                  const bucket = data.hourlyPattern.find(b => b.hour === h);
                  const total = bucket?.total ?? 0;
                  const failures = bucket?.failures ?? 0;
                  const maxTotal = Math.max(...data.hourlyPattern.map(b => b.total), 1);
                  const height = total > 0 ? Math.max((total / maxTotal) * 100, 4) : 2;
                  const failPct = total > 0 ? (failures / total) * 100 : 0;
                  return (
                    <div key={h} className="flex-1 flex flex-col items-center gap-0.5" title={`${h}:00 UTC — ${total} events, ${failures} failures`}>
                      <div className="w-full rounded-t relative overflow-hidden" style={{ height: `${height}%` }}>
                        <div className="absolute bottom-0 left-0 right-0 bg-blue-300 rounded-t" style={{ height: "100%" }} />
                        {failPct > 0 && (
                          <div className="absolute bottom-0 left-0 right-0 bg-red-400 rounded-t" style={{ height: `${failPct}%` }} />
                        )}
                      </div>
                      <span className="text-[9px] text-slate-400">{h}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-2 justify-center text-xs text-slate-400">
                <span className="flex items-center gap-1"><span className="w-3 h-2 bg-blue-300 rounded inline-block" /> Total</span>
                <span className="flex items-center gap-1"><span className="w-3 h-2 bg-red-400 rounded inline-block" /> Failures</span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="text-xs text-slate-400 text-center pt-2">
        Data from RoutingDecisionLog table (logged by messaging service). Extra Logging toggle updates Tenant.EnableExtraRoutingLog — no restart needed.
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function LiveMonitor() {
  const [tab, setTab] = React.useState<"keys" | "alerts" | "dashboard" | "synthetic" | "apinotices" | "routing">("keys");

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Live Monitor</h1>
          <p className="text-slate-500 text-sm mt-0.5">Velaro platform health — proactive alerts + real-time agent activity</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full shadow px-3 py-1.5">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs text-slate-600 font-medium">Live</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white rounded-lg shadow-sm p-1 w-fit flex-wrap">
        {([
          { id: "keys",       label: "Keys Status" },
          { id: "alerts",     label: "Platform Alerts" },
          { id: "apinotices", label: "API Notices" },
          { id: "synthetic",  label: "Synthetic Monitoring" },
          { id: "dashboard",  label: "Agent Dashboard" },
          { id: "routing",    label: "Routing Diagnostics" },
        ] as const).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "keys"       && <KeysPanel />}
      {tab === "alerts"     && <AlertsPanel />}
      {tab === "apinotices" && <ApiNoticesPanel />}
      {tab === "synthetic"  && <SyntheticPanel />}
      {tab === "dashboard"  && <DashboardPanel />}
      {tab === "routing"    && <RoutingDiagnosticsPanel />}
    </div>
  );
}
